import React, { useEffect, useState } from 'react';
import { useOrganization } from '@/clients/organization/useOrganization.ts';
import { SelectOrganization } from '@/shared/components/SelectOrganization.tsx';
import { EXCLUDED_PARTNER_ORGANIZATION_IDS } from '@/shared/constants/excludedPartnerOrganizations.ts';
import { useFormatters } from '@/shared/hooks/useFormatters/useFormatters.ts';
import { ReactHookFormDevTool } from '@/shared/components/ReactHookFormDevTool.tsx';
import {
    DISPOSAL_PURPOSE,
    DISPOSAL_PURPOSE_SCHEMA,
    FREIGHT_TYPE,
    FREIGHT_TYPE_SCHEMA,
    GetProductGroupListResponse,
    NestedOrderProductConfiguration,
    NestedOrderProductResponse,
    PRODUCT_CATEGORY,
    SERVICE_PRODUCT_TYPES_PLACEHOLDER,
} from '@schuettflix/interfaces';
import { DatePickerField, NumberField } from '@schuettflix/planum-react';
import { Combobox, TextField } from '@schuettflix/react-components';
import { Controller, FieldErrors, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { DeleteIcon } from '@schuettflix/icons-react';
import { FormattedActiveTaxClasses } from '@/clients/tax-classes/useTaxClasses.ts';
import {
    EnrichedHookFormError,
    enrichHookFormErrorForFulfillmentSectionErrors,
} from '@/CustomRequestProductChannel/utils/enrichHookFormErrorForFulfillmentSectionErrors.ts';
import { getProductCategoryIcon } from '@/CustomRequestProductChannel/utils/getProductCategoryIcon.ts';
import { useSupportedUnitNamesByProductType } from '@/CustomRequestProductChannel/hooks/useSupportedUnitNamesByProductType.ts';
import { cn } from '@/shared/utils/cn.ts';
import {
    CustomRequestProductSchema,
    useCustomRequestProductSchema,
} from '@/CustomRequestProductChannel/hooks/useCustomRequestProductSchema.ts';
import { usePresetFormValues } from '@/CustomRequestProductChannel/hooks/usePresetFormValues.ts';
import { CustomRequestProductHints } from '@/CustomRequestProductChannel/components/CustomRequestProductHints.tsx';
import { EmitterSubscriber } from '@/shared/hooks/useEmitter';
import { useLineItemListingSync } from '@/modules/fulfillment/hooks/useLineItemListingSync.ts';
import { format } from 'date-fns';
import { useOnFormValueUpdate } from '@/shared/hooks/useOnFormValueUpdate';
import { useLatest } from 'react-use';
import { isWasteFreightType } from '@/CustomRequestProductChannel/utils/isWasteFreightType.ts';
import {
    DISPOSAL_PURPOSE_DESCRIPTION,
    DISPOSAL_PURPOSE_LABEL_NAME,
} from '@/CustomRequestProductChannel/components/DisposalPurposeTranslationKeys.ts';
import { FREIGHT_TYPE_LABEL_NAME } from '@/shared/inputs/FreightTypeInput.tsx';
import { SUPPORTED_UNIT_LABEL } from '@/phrase/SupportedUnitLabels.ts';
import { PRODUCT_CATEGORY_LABEL } from '@/phrase/ProductCategoryLabels.ts';
import { getLimitedDateRanges } from '@/shared/utils/getLimitedDateRanges.ts';
import { formatDateTimezoneOffsetHotfix } from '@/tools/hotfix/formatDateTimezoneOffsetHotfix.ts';

const PRODUCT_NAME_PLACEHOLDER_LABEL = {
    MATERIAL: 'product.category.material',
    TRANSPORT: 'product.category.transport',
    MATERIAL_SERVICE: 'product.category.custom',
    TRANSPORT_SERVICE: 'product.category.custom',
} as const satisfies Record<PRODUCT_CATEGORY, string>;

const SERVICE_PRODUCT_NAME_PLACEHOLDER = {
    CUSTOM_SERVICE: 'product.service.customService.placeholder',
    CUSTOM_SERVICE_TRANSPORT: 'product.service.customServiceTransport.placeholder',
} as const satisfies Record<SERVICE_PRODUCT_TYPES_PLACEHOLDER, string>;

export type CustomRequestProductConfiguration = Omit<NestedOrderProductConfiguration, 'orderProductId'>;
export type CustomRequestProduct = Pick<
    NestedOrderProductResponse,
    'id' | 'category' | 'type' | 'positionProductId' | 'index'
> & { productConfiguration: CustomRequestProductConfiguration };
export type CustomRequestProductForm = CustomRequestProductSchema;
export type FormErrors = FieldErrors<CustomRequestProductForm>;
type FieldErrorKey = keyof CustomRequestProductForm;

export type CustomRequestProductQuote = Pick<
    NonNullable<NestedOrderProductResponse['quote']>,
    | 'partnerOrganizationId'
    | 'unit'
    | 'purchasePrice'
    | 'salesPrice'
    | 'purchaseTaxClassId'
    | 'currencyCode'
    | 'salesTaxClassId'
> & {
    id?: string;
    amount?: number;
    serviceDate?: NonNullable<NestedOrderProductResponse['quote']>['serviceDate'];
    name: string;
    freightType?: FREIGHT_TYPE;
    disposalPurpose?: DISPOSAL_PURPOSE;
};

type FormFieldSettings = Partial<
    Record<
        keyof CustomRequestProductQuote,
        {
            // to overwrite the label of the field
            label?: string;
            // to make the field optional in the validation
            isOptional?: boolean;
            // to hide the field in the UI
            isHidden?: boolean;
            isInvalid?: boolean;
        }
    >
>;

export interface BaseCustomRequestProductProps {
    // To intersect the schema with the form schema (extend schema)
    intersectionSchema?: z.ZodObject<{
        salesPrice: z.ZodEffects<z.ZodString, string, string>;
    }>;
    // Leading values are always overwriting the form values
    leadingValues?: Partial<Omit<CustomRequestProductForm, 'currencyCode'>>;
    product: CustomRequestProduct;
    quote?: CustomRequestProductQuote;
    customerId: number;
    showValidation: boolean; // shared
    showProductPriceAddedHint?: boolean; // shared
    showProductPriceNotAddedHint?: boolean; // shared
    showZeroPriceProductConfiguration?: boolean; // shared
    enableDelete?: boolean; // shared
    disabled?: boolean; // shared
    disabledFields?: Partial<Record<keyof Omit<CustomRequestProductForm, 'currencyCode'>, boolean>>;
    taxInfo?: FormattedActiveTaxClasses;
    formFieldSettings?: FormFieldSettings;
    onFormChange?: (
        form: (
            | { values: Partial<CustomRequestProductForm>; isValid: false }
            | { values: CustomRequestProductForm; isValid: true }
        ) & {
            productId: CustomRequestProduct['id'];
        }
    ) => void; // shared
    onBeforeFormChange?: (values: Partial<CustomRequestProductForm>) => void; // shared
    onDeleteProduct?: (productId: string) => void; // shared
    onUpdateProductConfiguration?: (values: Partial<CustomRequestProductConfiguration>) => void; //shared
    templateId: GetProductGroupListResponse['templateId']; // shared
    restrictFutureServiceDate?: boolean; // shared
    onUpdateShowValidation?: (showValidation: boolean) => void; // shared
    onErrorsChange?: (errors: EnrichedHookFormError[]) => void;
    onIsDirtyChange?: (isDirty: boolean) => void;
    resetEmitter?: EmitterSubscriber<'preset' | 'zero'>;
}

export function CustomRequestProduct({
    intersectionSchema,
    leadingValues,
    product,
    quote,
    formFieldSettings,
    templateId,
    customerId,
    taxInfo,
    showValidation,
    showProductPriceAddedHint = false,
    showProductPriceNotAddedHint = false,
    showZeroPriceProductConfiguration = true,
    enableDelete,
    disabled = false,
    disabledFields = {},
    onFormChange,
    onBeforeFormChange,
    onDeleteProduct,
    onErrorsChange,
    restrictFutureServiceDate = false,
    onUpdateProductConfiguration,
    onIsDirtyChange,
    resetEmitter,
    onUpdateShowValidation,
}: BaseCustomRequestProductProps) {
    const { t } = useTranslation();
    const { data: organization } = useOrganization(customerId);
    const { getCurrencySymbol } = useFormatters();
    const { data: supportedUnitNames } = useSupportedUnitNamesByProductType(product.type);

    const customRequestProductSchema = useCustomRequestProductSchema(product);
    const customRequestFormSchema = intersectionSchema
        ? z.intersection(customRequestProductSchema, intersectionSchema)
        : customRequestProductSchema;

    const {
        handleSubmit,
        control,
        formState,
        register,
        reset,
        trigger,
        setValue,
        getValues,
        setFocus,
        getFieldState,
        resetField,
    } = useFormContext<CustomRequestProductSchema>();

    const { isLineItemListingActive, isServiceDateRange, serviceDateRangeForDatePicker } = useLineItemListingSync({
        productId: product.id,
        onAmountChange: amount => setValue('amount', amount, { shouldValidate: true }),
        onServiceDateRangeChange: serviceDateRange => {
            if (!serviceDateRange.length) return setValue('serviceDate', formState.defaultValues?.serviceDate || '');
            setValue('serviceDate', format(serviceDateRange[1], 'yyyy-MM-dd'), { shouldValidate: true });
        },
    });

    const [disposalPurposeFormFieldSettings, setDisposalPurposeFormFieldSettings] = useState<
        FormFieldSettings['disposalPurpose']
    >(formFieldSettings?.disposalPurpose);

    const { presetValues } = usePresetFormValues({
        leadingValues,
        product,
        quote,
        formFieldSettings,
        templateId,
        customerId,
        taxInfo,
    });

    useEffect(() => {
        if (!presetValues) {
            return;
        }

        // NOTE: using set timeout to delay the reset, since other components are rendered at the same time which produces the following warning
        // Warning: Cannot update a component (`DevTool`) while rendering a different component (`CustomRequestProduct`)
        const timeout = setTimeout(() => {
            reset(presetValues);
            // a reset does not trigger the form change, so we need to do it manually
            triggerFormChange.current(presetValues);
        }, 200);

        return () => {
            clearTimeout(timeout);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presetValues]);

    const { errors, isDirty } = formState;

    useEffect(() => {
        onIsDirtyChange?.(isDirty);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDirty]);

    const formValues = getValues();
    const { data: partnerOrganization } = useOrganization(formValues.partnerOrganizationId);

    useEffect(
        () =>
            resetEmitter?.subscribe(mode =>
                reset({
                    ...presetValues,
                    amount: mode === 'zero' ? 0 : presetValues?.amount,
                    serviceDate: mode === 'zero' ? '' : presetValues?.serviceDate,
                    partnerOrganizationId: partnerOrganization?.id,
                    purchasePrice: formValues.purchasePrice,
                    salesPrice: formValues.salesPrice,
                    unit: formValues.unit,
                    productName: formValues.productName,
                })
            ),
        [
            resetEmitter,
            presetValues,
            reset,
            partnerOrganization?.id,
            formValues.purchasePrice,
            formValues.salesPrice,
            formValues.unit,
            formValues.productName,
        ]
    );

    const { triggerUpdate } = useOnFormValueUpdate<CustomRequestProductSchema>(values => {
        onBeforeFormChange?.(values);
        try {
            customRequestFormSchema.parse(values);
            if (values.serviceDate && validateServiceDateConsideringFutureRestriction(values.serviceDate)) {
                throw new Error('Service date is in the future');
            }
            void handleSubmit(
                // fires when the form is really valid without errors
                values => {
                    onFormChange?.({ values, isValid: true, productId: product.id });
                },
                // fires when the form is invalid and has errors (and shows validation to the user)
                () => {
                    onFormChange?.({ values, isValid: false, productId: product.id });
                }
            )();
            // In case the schema parsing failed we know that the current form is invalid
            // the parse() throws an error
        } catch {
            onFormChange?.({ values, isValid: false, productId: product.id });
        }
    });
    const triggerFormChange = useLatest(triggerUpdate);

    useEffect(() => {
        const values = getValues();
        onBeforeFormChange?.(values);
        try {
            if (values.serviceDate && validateServiceDateConsideringFutureRestriction(values.serviceDate)) {
                throw new Error('Service date is in the future');
            }
            customRequestFormSchema.parse(values);
            onFormChange?.({ values, isValid: true, productId: product.id });
        } catch {
            onFormChange?.({ values, isValid: false, productId: product.id });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validateServiceDateConsideringFutureRestriction = (date: string) => {
        return restrictFutureServiceDate && date && new Date(date) > new Date();
    };

    const getFormError = (fieldName: FieldErrorKey) => {
        return errors[fieldName];
    };

    const getFormErrorMessage = (fieldName: FieldErrorKey) => {
        const formError = getFormError(fieldName);
        return formError?.message;
    };

    const isNegativePurchasePrice = Number(formValues.purchasePrice) < 0;
    const isNegativeSalesPrice = Number(formValues.salesPrice) < 0;
    const isZeroPurchasePrice = Number(formValues.purchasePrice) === 0;
    const isZeroSalesPrice = Number(formValues.salesPrice) === 0;

    useEffect(() => {
        onErrorsChange?.(enrichHookFormErrorForFulfillmentSectionErrors({ errors, setFocus }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        errors.salesPrice,
        errors.purchasePrice,
        errors.amount,
        errors.unit,
        errors.productName,
        errors.partnerOrganizationId,
        errors.serviceDate,
        errors.purchaseTaxClassId,
        errors.salesTaxClassId,
        errors.freightType,
        errors.disposalPurpose,
    ]);

    useEffect(() => {
        if (showValidation) {
            trigger().catch(e => console.error(e));
        }
    }, [showValidation, trigger]);

    useEffect(() => {
        if (!leadingValues) return;

        Object.entries(leadingValues).map(([key, value]) => {
            if (!value) return;
            setValue(key as keyof typeof leadingValues, value, { shouldDirty: true, shouldValidate: true });
        });

        // somehow `leadingValue` is losing its reference, so we cant add it to the dependency array, needs some deeper investigation
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        leadingValues?.amount,
        leadingValues?.unit,
        leadingValues?.serviceDate,
        leadingValues?.partnerOrganizationId,
        leadingValues?.purchaseTaxClassId,
        leadingValues?.salesTaxClassId,
        leadingValues?.productName,
        leadingValues?.freightType,
        leadingValues?.disposalPurpose,
        setValue,
    ]);

    useEffect(() => {
        if (getFieldState('salesPrice').isDirty) {
            trigger('salesPrice').catch(e => console.error(e));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formFieldSettings?.salesPrice?.isInvalid]);

    useEffect(() => {
        if (!formValues.freightType || formFieldSettings?.freightType?.isHidden) {
            return;
        }

        if (!isWasteFreightType(formValues.freightType)) {
            resetField('disposalPurpose');
        }

        setDisposalPurposeFormFieldSettings(prevFieldSettings => ({
            ...prevFieldSettings,
            isHidden: !isWasteFreightType(formValues.freightType),
            isOptional: !isWasteFreightType(formValues.freightType),
        }));

        if (isWasteFreightType(formValues.freightType)) {
            onUpdateShowValidation?.(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues.freightType]);

    const isFormFieldVisible = (field: keyof CustomRequestProductQuote) => {
        if (field === 'disposalPurpose') {
            return !disposalPurposeFormFieldSettings?.isHidden;
        }

        return !formFieldSettings?.[field]?.isHidden;
    };

    const PriceFieldSuffix = () => {
        const unit = getValues('unit');

        return (
            <span>
                {getCurrencySymbol(organization?.market.currencyCode)}
                {unit ? <>&nbsp;/&nbsp;{t(SUPPORTED_UNIT_LABEL[unit])}</> : null}
            </span>
        );
    };

    const configurableProductCharacteristicsCount = [
        isFormFieldVisible('freightType'),
        isFormFieldVisible('disposalPurpose'),
    ].filter(c => c).length;

    if (!organization) return null;

    return (
        <div
            className="@container/product [counter-increment:productCounter] first:pt-0"
            data-test={`manual-product-configuration-form-${product.category}-${product.type}`}
        >
            <form className="group">
                <div className="flex justify-between pb-4">
                    <p className="font-copy-sm-strong uppercase before:content-[counter(productCounter)'.\0020']">
                        {t(PRODUCT_CATEGORY_LABEL[product.category])}
                    </p>
                    <div className="flex items-center justify-center">
                        <DeleteIcon
                            monochrome
                            className={!enableDelete ? 'cursor-not-allowed' : 'cursor-pointer'}
                            disabled={!enableDelete}
                            onClick={() => onDeleteProduct?.(product.id)}
                        />
                    </div>
                </div>
                <div
                    className={cn('@lg:grid-cols-2 @3xl:grid-cols-3 grid gap-4', {
                        '@6xl:grid-cols-5': isFormFieldVisible('serviceDate'),
                        '@6xl:grid-cols-4': !isFormFieldVisible('serviceDate'),
                    })}
                >
                    <TextField
                        {...register('productName')}
                        className={cn('@lg:col-span-full @6xl:col-span-2', {
                            '@3xl:col-span-1': configurableProductCharacteristicsCount > 1,
                            '@3xl:col-span-2': configurableProductCharacteristicsCount === 1,
                            '@3xl:col-span-3': configurableProductCharacteristicsCount === 0,
                        })}
                        variant="sm"
                        placeholder={
                            product.type in SERVICE_PRODUCT_NAME_PLACEHOLDER
                                ? t(SERVICE_PRODUCT_NAME_PLACEHOLDER[product.type as SERVICE_PRODUCT_TYPES_PLACEHOLDER])
                                : t(PRODUCT_NAME_PLACEHOLDER_LABEL[product.category])
                        }
                        isCritical={!!getFormError('productName')}
                        disabled={disabled || disabledFields.productName}
                        errorMessage={getFormErrorMessage('productName')}
                        leadingIcon={getProductCategoryIcon(product.category)}
                        data-test={`order-product-name-${product.category}`}
                    />
                    {isFormFieldVisible('freightType') && (
                        <div className="@lg:col-span-2 @3xl:col-span-1">
                            <Controller
                                render={({ field: { ref: _, ...field } }) => (
                                    <Combobox
                                        {...field}
                                        options={FREIGHT_TYPE_SCHEMA.options.map(freightType => ({
                                            value: freightType,
                                            label: t(FREIGHT_TYPE_LABEL_NAME[freightType]),
                                        }))}
                                        placeholder={t('product.quote.freightType')}
                                        disabled={disabledFields.freightType || disabled}
                                        errorMessage={getFormErrorMessage('freightType')}
                                        className={cn('flex-grow', {
                                            'mb-4': !!getFormError('freightType'),
                                        })}
                                        variant="sm"
                                        dataTest={`order-product-freight-type`}
                                        optionsClassName="min-w-[200px] z-30"
                                    />
                                )}
                                defaultValue={formValues.freightType ?? null}
                                name={'freightType'}
                                control={control}
                            />
                        </div>
                    )}
                    {isFormFieldVisible('disposalPurpose') && (
                        <div className="@lg:col-span-2 @3xl:col-span-1">
                            <Controller
                                render={({ field: { ref: _, ...field } }) => (
                                    <Combobox
                                        {...field}
                                        options={DISPOSAL_PURPOSE_SCHEMA.options.map(disposalPurpose => ({
                                            value: disposalPurpose,
                                            label: t(DISPOSAL_PURPOSE_LABEL_NAME[disposalPurpose]),
                                            description: t(DISPOSAL_PURPOSE_DESCRIPTION[disposalPurpose]),
                                        }))}
                                        placeholder={t('product.quote.disposalPurpose.placeholder')}
                                        disabled={disabledFields.disposalPurpose || disabled}
                                        errorMessage={getFormErrorMessage('disposalPurpose')}
                                        className={cn('flex-grow', {
                                            'mb-4': !!getFormError('disposalPurpose'),
                                        })}
                                        variant="sm"
                                        optionsClassName={cn('@6xl:min-w-[calc(200%)] min-w-[200px] text-start z-30', {
                                            '@6xl:min-w-[200px]': !isFormFieldVisible('serviceDate'),
                                        })}
                                    />
                                )}
                                defaultValue={formValues.disposalPurpose ?? null}
                                name={'disposalPurpose'}
                                control={control}
                            />
                        </div>
                    )}
                    <div className="@3xl:col-start-1">
                        <div className="grid grid-cols-2 gap-1">
                            <Controller
                                render={({ field: { onChange, value, ...field } }) => (
                                    <NumberField
                                        {...field}
                                        value={value}
                                        onValueChange={onChange}
                                        className="min-h-12 w-full"
                                        size="lg"
                                        disabled={disabledFields.amount || disabled}
                                        error={getFormErrorMessage('amount')}
                                        step={0.001}
                                        placeholder={formFieldSettings?.amount?.label ?? t('product.quote.quantity')}
                                        aria-label={formFieldSettings?.amount?.label ?? t('product.quote.quantity')}
                                        data-test={`order-product-amount-${product.category}`}
                                    />
                                )}
                                name="amount"
                                control={control}
                            />
                            <Controller
                                name="unit"
                                control={control}
                                render={({ field: { ref: _, ...field } }) => (
                                    <Combobox
                                        className={`cy-order-product-unit-${product.category}`}
                                        {...field}
                                        options={(supportedUnitNames ?? []).map(unit => {
                                            return {
                                                value: unit,
                                                label: t(SUPPORTED_UNIT_LABEL[unit]),
                                            };
                                        })}
                                        placeholder={t('product.quote.unit')}
                                        disabled={disabledFields.unit || disabled}
                                        variant="sm"
                                        errorMessage={getFormErrorMessage('unit')}
                                        optionsClassName="min-w-[200px] z-20"
                                        dataTest={'unit-select-input'}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className="@3xl:col-span-2 @6xl:col-span-1">
                        <Controller
                            name={'partnerOrganizationId'}
                            control={control}
                            render={({ field, fieldState }) => (
                                <SelectOrganization
                                    {...field}
                                    selectedOrganizationId={field.value}
                                    placeholder={t('product.addPosition.partner.label')}
                                    filters={{
                                        isActive: true,
                                        isBlocked: false,
                                        status: ['approved'],
                                    }}
                                    critical={!!fieldState.error}
                                    helperText={
                                        fieldState.error?.message ??
                                        t('product.addPosition.errorMessage.pleaseEnterValue')
                                    }
                                    disabled={disabledFields.partnerOrganizationId || disabled}
                                    size="sm"
                                    excludeMatchingIds={{ id: customerId }}
                                    excludeIds={EXCLUDED_PARTNER_ORGANIZATION_IDS}
                                    dataTest={`order-product-partner-organization-${product.category}`}
                                />
                            )}
                        />
                    </div>
                    <div className="grid grid-cols-3 gap-1">
                        <Controller
                            name="purchasePrice"
                            control={control}
                            render={({ field: { value, onChange, ...field } }) => (
                                <NumberField
                                    {...field}
                                    value={value ? Number(value) : undefined}
                                    onValueChange={value => {
                                        // todo: the form should use a string for this value
                                        onChange(value?.toString(10));
                                    }}
                                    className="col-span-2 min-h-12 w-full"
                                    size="lg"
                                    trailingSlot={<PriceFieldSuffix />}
                                    disabled={disabledFields.purchasePrice || disabled}
                                    error={getFormErrorMessage('purchasePrice')}
                                    placeholder={t('product.quote.purchasePrice')}
                                    aria-label={t('product.quote.purchasePrice')}
                                    step={0.01}
                                    data-test={`order-product-purchase-price-${product.category}`}
                                />
                            )}
                        />

                        <Controller
                            name="purchaseTaxClassId"
                            control={control}
                            render={({ field: { ref: _, ...field } }) => (
                                <Combobox
                                    className="order-product-purchase-tax-class flex-none"
                                    {...field}
                                    options={taxInfo?.taxClassOptions || []}
                                    placeholder={t('product.quote.tax')}
                                    disabled={disabledFields.purchaseTaxClassId || disabled}
                                    variant="sm"
                                    errorMessage={getFormErrorMessage('purchaseTaxClassId')}
                                    optionsClassName="min-w-[200px] z-20"
                                    dataTest={`order-product-purchase-tax-class`}
                                />
                            )}
                        />
                    </div>
                    <div className="grid grid-cols-3 gap-1">
                        <div className="col-span-2">
                            <Controller
                                name="salesPrice"
                                control={control}
                                render={({ field: { onChange, value, ...field } }) => (
                                    <NumberField
                                        {...field}
                                        value={value ? Number(value) : undefined}
                                        onValueChange={value => {
                                            // todo: the form should use a string for this value
                                            onChange(value?.toString(10));
                                        }}
                                        className="min-h-12 w-full"
                                        size="lg"
                                        trailingSlot={<PriceFieldSuffix />}
                                        disabled={disabledFields.salesPrice || disabled}
                                        error={getFormErrorMessage('salesPrice')}
                                        placeholder={t('product.quote.salesPrice')}
                                        aria-label={t('product.quote.salesPrice')}
                                        step={0.01}
                                        data-test={`order-product-sales-price-${product.category}`}
                                    />
                                )}
                            />
                        </div>
                        <Controller
                            name="salesTaxClassId"
                            control={control}
                            render={({ field: { ref: _, ...field } }) => (
                                <Combobox
                                    className="order-product-sales-tax-class flex-none"
                                    {...field}
                                    options={taxInfo?.taxClassOptions || []}
                                    placeholder={t('product.quote.tax')}
                                    disabled={disabledFields.salesTaxClassId || disabled}
                                    variant="sm"
                                    errorMessage={getFormErrorMessage('salesTaxClassId')}
                                    optionsClassName="min-w-[200px] z-20"
                                    dataTest={`order-product-sales-tax-class`}
                                />
                            )}
                        />
                    </div>

                    {isFormFieldVisible('serviceDate') && (
                        <div data-test={`date-picker-${product.category}`}>
                            <Controller
                                name="serviceDate"
                                control={control}
                                render={({ field: { value, onChange } }) => {
                                    const { minDate, maxDate } = getLimitedDateRanges();

                                    return isLineItemListingActive && isServiceDateRange ? (
                                        <DatePickerField
                                            mode="range"
                                            selected={serviceDateRangeForDatePicker}
                                            size="lg"
                                            placeholder={t('product.quote.serviceDate')}
                                            disabled={disabled || disabledFields.serviceDate}
                                            data-test={`order-product-service-date-${product.category}`}
                                        />
                                    ) : (
                                        <DatePickerField
                                            selected={value ? new Date(Date.parse(value)) : undefined}
                                            mode="single"
                                            minDate={minDate}
                                            maxDate={maxDate}
                                            onSelect={date => {
                                                const value = date ? formatDateTimezoneOffsetHotfix(date) : undefined;
                                                onChange(value);
                                            }}
                                            size="lg"
                                            placeholder={t('product.quote.serviceDate')}
                                            disabled={disabled || disabledFields.serviceDate}
                                            data-test={`order-product-service-date-${product.category}`}
                                            error={
                                                getFormErrorMessage('serviceDate') ??
                                                (validateServiceDateConsideringFutureRestriction(formValues.serviceDate)
                                                    ? t('product.addPosition.errorMessage.serviceDateInFuture')
                                                    : undefined)
                                            }
                                        />
                                    );
                                }}
                            />
                        </div>
                    )}
                </div>
                <CustomRequestProductHints
                    isNegativePurchasePrice={isNegativePurchasePrice}
                    isNegativeSalesPrice={isNegativeSalesPrice}
                    isZeroPurchasePrice={isZeroPurchasePrice}
                    isZeroSalesPrice={isZeroSalesPrice}
                    productConfiguration={product.productConfiguration}
                    partnerOrganizationName={partnerOrganization?.name}
                    orderingOrganizationName={organization?.name}
                    onUpdateProductConfiguration={onUpdateProductConfiguration}
                    showProductPriceNotAddedHint={showProductPriceNotAddedHint}
                    showProductPriceAddedHint={showProductPriceAddedHint}
                    showZeroPriceProductConfiguration={showZeroPriceProductConfiguration}
                />
            </form>
            <ReactHookFormDevTool control={control} />
        </div>
    );
}
